import {
  Button,
  ButtonGroup,
  Dialog,
  Field,
  HStack,
  Icon,
  Input,
  Portal,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  createListCollection,
} from '@chakra-ui/react';
import { toaster } from '@mono/chakra';
import { isHttpError, useHttp, useHttpQuery } from '@mono/http-hook';
import { FC, useEffect, useMemo, useState } from 'react';
import { FiUser, FiUsers } from 'react-icons/fi';
import { ProjectSummary } from '../../projects/hooks';
import { TeamDetails } from '../../teams/hooks';
import { OrganizationUser } from '../../users/hooks';
import { ProjectRoleValue, PROJECT_ROLE_OPTIONS } from '../types';

const roleCollection = createListCollection({
  items: PROJECT_ROLE_OPTIONS,
});

const getErrorMessage = (error: unknown, fallback: string) => {
  if (isHttpError(error)) {
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object') {
      const { error: errorMessage, message: responseMessage } =
        responseData as { error?: string; message?: string };
      return errorMessage ?? responseMessage ?? error.message ?? fallback;
    }
    return error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

type AddCollaboratorsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  project: ProjectSummary;
  collaboratorUserIds: Set<string>;
  collaboratorTeamIds: Set<string>;
  onAdded: () => Promise<void> | void;
};

const AddCollaboratorsModal: FC<AddCollaboratorsModalProps> = ({
  isOpen,
  onClose,
  project,
  collaboratorUserIds,
  collaboratorTeamIds,
  onAdded,
}) => {
  const http = useHttp();
  const [mode, setMode] = useState<'users' | 'teams'>('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleSelections, setRoleSelections] = useState<
    Record<string, ProjectRoleValue>
  >({});
  const [pendingAdd, setPendingAdd] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen) {
      setMode('users');
      setSearchQuery('');
      setRoleSelections({});
      setPendingAdd(new Set());
    }
  }, [isOpen]);

  const usersQuery = useHttpQuery<{ members: OrganizationUser[] }>({
    queryKey: ['organization-users', project.organizationId],
    config: {
      method: 'get',
      url: `/api/v1/organizations/${project.organizationId}/users`,
      withCredentials: true,
    },
    options: {
      enabled: isOpen,
    },
  });

  const teamsQuery = useHttpQuery<{ teams: TeamDetails[] }>({
    queryKey: ['organization-teams', project.organizationId],
    config: {
      method: 'get',
      url: `/api/v1/organizations/${project.organizationId}/teams`,
      withCredentials: true,
    },
    options: {
      enabled: isOpen,
    },
  });

  const filteredUsers = useMemo(() => {
    const members = usersQuery.data?.members ?? [];
    if (!searchQuery.trim()) {
      return members;
    }
    const query = searchQuery.trim().toLowerCase();
    return members.filter((member) => {
      const name = member.user.name ?? '';
      const email = member.user.email ?? '';
      return (
        name.toLowerCase().includes(query) ||
        email.toLowerCase().includes(query)
      );
    });
  }, [usersQuery.data?.members, searchQuery]);

  const filteredTeams = useMemo(() => {
    const teams = teamsQuery.data?.teams ?? [];
    if (!searchQuery.trim()) {
      return teams;
    }
    const query = searchQuery.trim().toLowerCase();
    return teams.filter((team) => team.name.toLowerCase().includes(query));
  }, [teamsQuery.data?.teams, searchQuery]);

  const handleAddCollaborator = async (
    type: 'user' | 'team',
    targetId: string,
    role: ProjectRoleValue
  ) => {
    const key = `${type}:${targetId}`;

    setPendingAdd((prev) => {
      const updated = new Set(prev);
      updated.add(key);
      return updated;
    });

    try {
      const payload =
        type === 'user'
          ? { userId: targetId, role }
          : { teamId: targetId, role };

      await http.post(
        `/api/v1/organizations/${project.organizationId}/projects/${project.id}/collaborators`,
        payload,
        {
          withCredentials: true,
        }
      );

      setRoleSelections((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });

      await onAdded();

      toaster.create({
        type: 'success',
        title: 'Collaborator added',
        description:
          type === 'user'
            ? 'The user now has access to this project.'
            : 'The team now has access to this project.',
      });
    } catch (error) {
      const message = getErrorMessage(
        error,
        'Unable to update collaborators right now.'
      );
      toaster.create({
        type: 'error',
        title: 'Failed to update collaborators',
        description: message,
      });
    } finally {
      setPendingAdd((prev) => {
        const updated = new Set(prev);
        updated.delete(key);
        return updated;
      });
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxW="5xl" w="full">
            <Dialog.CloseTrigger />
            <Dialog.Header gap="1" flexDirection={'column'}>
              <Dialog.Title>Add collaborators</Dialog.Title>
              <Dialog.Description>
                Invite people or teams from your organization to join{' '}
                {project.name}.
              </Dialog.Description>
            </Dialog.Header>
            <Dialog.Body>
              <Stack gap="4">
                <ButtonGroup size="sm" alignSelf="flex-start">
                  <Button
                    variant={mode === 'users' ? 'solid' : 'outline'}
                    onClick={() => setMode('users')}
                    display="inline-flex"
                    alignItems="center"
                    gap="2"
                  >
                    <Icon as={FiUser} />
                    People
                  </Button>
                  <Button
                    variant={mode === 'teams' ? 'solid' : 'outline'}
                    onClick={() => setMode('teams')}
                    display="inline-flex"
                    alignItems="center"
                    gap="2"
                  >
                    <Icon as={FiUsers} />
                    Teams
                  </Button>
                </ButtonGroup>

                <Field.Root>
                  <Field.Label srOnly>Search collaborators</Field.Label>
                  <Input
                    placeholder={`Search ${
                      mode === 'users' ? 'people' : 'teams'
                    } by name`}
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                  />
                </Field.Root>

                {mode === 'users' ? (
                  usersQuery.isLoading ? (
                    <Stack gap="3">
                      <Skeleton height="44px" borderRadius="md" />
                      <Skeleton height="44px" borderRadius="md" />
                    </Stack>
                  ) : filteredUsers.length === 0 ? (
                    <Text color="fg.muted" fontSize="sm">
                      {searchQuery
                        ? 'No matching people found.'
                        : 'No members found in this organization.'}
                    </Text>
                  ) : (
                    <Table.ScrollArea maxH="360px">
                      <Table.Root>
                        <Table.Header>
                          <Table.Row>
                            <Table.ColumnHeader>Person</Table.ColumnHeader>
                            <Table.ColumnHeader>Email</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="center">
                              Access level
                            </Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right">
                              Action
                            </Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {filteredUsers.map((member) => {
                            const userId = member.user.id;
                            const alreadyAdded =
                              collaboratorUserIds.has(userId);
                            const key = `user:${userId}`;
                            const selectedRole: ProjectRoleValue =
                              roleSelections[key] ?? 'READ';
                            const pending = pendingAdd.has(key);
                            const displayName =
                              member.user.name ??
                              member.user.email ??
                              'Unknown user';

                            return (
                              <Table.Row key={userId}>
                                <Table.Cell>
                                  <HStack gap="2">
                                    <Icon as={FiUser} />
                                    <Text fontWeight="medium">
                                      {displayName}
                                    </Text>
                                  </HStack>
                                </Table.Cell>
                                <Table.Cell>
                                  <Text fontSize="sm" color="fg.muted">
                                    {member.user.email ?? 'No email provided'}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell textAlign="center">
                                  <Select.Root
                                    collection={roleCollection}
                                    value={[selectedRole]}
                                    onValueChange={(details) => {
                                      const [value] = details.value as string[];
                                      if (value) {
                                        setRoleSelections((prev) => ({
                                          ...prev,
                                          [key]: value as ProjectRoleValue,
                                        }));
                                      }
                                    }}
                                    size="sm"
                                    disabled={pending}
                                  >
                                    <Select.HiddenSelect />
                                    <Select.Control>
                                      <Select.Trigger>
                                        <Select.ValueText placeholder="Select role" />
                                        <Select.Indicator />
                                      </Select.Trigger>
                                    </Select.Control>
                                    <Select.Positioner>
                                      <Select.Content>
                                        {roleCollection.items.map(
                                          (roleItem) => (
                                            <Select.Item
                                              item={roleItem}
                                              key={roleItem.value}
                                            >
                                              {roleItem.label}
                                              <Select.ItemIndicator />
                                            </Select.Item>
                                          )
                                        )}
                                      </Select.Content>
                                    </Select.Positioner>
                                  </Select.Root>
                                </Table.Cell>
                                <Table.Cell textAlign="right">
                                  <Button
                                    colorPalette="primary"
                                    size="sm"
                                    onClick={() =>
                                      void handleAddCollaborator(
                                        'user',
                                        userId,
                                        selectedRole
                                      )
                                    }
                                    loading={pending}
                                    disabled={alreadyAdded}
                                  >
                                    {alreadyAdded
                                      ? 'Already added'
                                      : 'Add user'}
                                  </Button>
                                </Table.Cell>
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table.Root>
                    </Table.ScrollArea>
                  )
                ) : teamsQuery.isLoading ? (
                  <Stack gap="3">
                    <Skeleton height="44px" borderRadius="md" />
                    <Skeleton height="44px" borderRadius="md" />
                  </Stack>
                ) : filteredTeams.length === 0 ? (
                  <Text color="fg.muted" fontSize="sm">
                    {searchQuery
                      ? 'No matching teams found.'
                      : 'No teams created yet.'}
                  </Text>
                ) : (
                  <Table.ScrollArea maxH="360px">
                    <Table.Root>
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Team</Table.ColumnHeader>
                          <Table.ColumnHeader>Members</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="center">
                            Access level
                          </Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="right">
                            Action
                          </Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {filteredTeams.map((team) => {
                          const teamId = team.id;
                          const alreadyAdded = collaboratorTeamIds.has(teamId);
                          const key = `team:${teamId}`;
                          const selectedRole: ProjectRoleValue =
                            roleSelections[key] ?? 'READ';
                          const pending = pendingAdd.has(key);

                          return (
                            <Table.Row key={teamId}>
                              <Table.Cell>
                                <HStack gap="2">
                                  <Icon as={FiUsers} />
                                  <Text fontWeight="medium">{team.name}</Text>
                                </HStack>
                              </Table.Cell>
                              <Table.Cell>
                                <Text fontSize="sm" color="fg.muted">
                                  {team.membersCount} member
                                  {team.membersCount === 1 ? '' : 's'}
                                </Text>
                              </Table.Cell>
                              <Table.Cell textAlign="center">
                                <Select.Root
                                  collection={roleCollection}
                                  value={[selectedRole]}
                                  onValueChange={(details) => {
                                    const [value] = details.value as string[];
                                    if (value) {
                                      setRoleSelections((prev) => ({
                                        ...prev,
                                        [key]: value as ProjectRoleValue,
                                      }));
                                    }
                                  }}
                                  size="sm"
                                  disabled={pending}
                                >
                                  <Select.HiddenSelect />
                                  <Select.Control>
                                    <Select.Trigger>
                                      <Select.ValueText placeholder="Select role" />
                                      <Select.Indicator />
                                    </Select.Trigger>
                                  </Select.Control>
                                  <Select.Positioner>
                                    <Select.Content>
                                      {roleCollection.items.map((roleItem) => (
                                        <Select.Item
                                          item={roleItem}
                                          key={roleItem.value}
                                        >
                                          {roleItem.label}
                                          <Select.ItemIndicator />
                                        </Select.Item>
                                      ))}
                                    </Select.Content>
                                  </Select.Positioner>
                                </Select.Root>
                              </Table.Cell>
                              <Table.Cell textAlign="right">
                                <Button
                                  colorPalette="primary"
                                  size="sm"
                                  onClick={() =>
                                    void handleAddCollaborator(
                                      'team',
                                      teamId,
                                      selectedRole
                                    )
                                  }
                                  loading={pending}
                                  disabled={alreadyAdded}
                                >
                                  {alreadyAdded ? 'Already added' : 'Add team'}
                                </Button>
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table.Root>
                  </Table.ScrollArea>
                )}
              </Stack>
            </Dialog.Body>
            <Dialog.Footer justifyContent="flex-end">
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export type { AddCollaboratorsModalProps };
export default AddCollaboratorsModal;
